import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { useLang } from "@/LanguageContext";
import { trackEvent } from "@/lib/track";

export default function Navbar() {
  const { t, lang, toggle } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === "/";
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const solid = scrolled || !isHome;

  const goToSection = (id) => {
    setOpen(false);
    if (isHome) {
      const el = document.getElementById(id);
      if (!el) return;
      if (window.__lenis) window.__lenis.scrollTo(el, { offset: -20 });
      else el.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate(`/#${id}`);
    }
  };

  const goToPage = (path) => {
    setOpen(false);
    navigate(path);
    window.scrollTo(0, 0);
  };

  const sections = [
    { id: "about", label: t.nav.about },
    { id: "destinations", label: t.nav.destinations },
    { id: "why", label: t.nav.why },
  ];
  const pages = [
    { path: "/tarifs", label: t.nav.tarifs },
    { path: "/partenaires", label: t.nav.partners },
    { path: "/espace-client", label: t.nav.client },
  ];

  return (
    <motion.nav
      data-testid="navbar"
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        solid ? "bg-navy/95 backdrop-blur-md py-3 shadow-lg shadow-navy/20" : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex items-center justify-between">
        <button data-testid="nav-logo" onClick={() => goToPage("/")} className="flex items-center gap-3">
          <img src="/logo-mark.png" alt="French Touch Concierge" className="h-14 md:h-16 w-auto object-contain" />
        </button>

        <div className="hidden lg:flex items-center gap-7">
          {sections.map((l) => (
            <button key={l.id} data-testid={`nav-${l.id}`} onClick={() => goToSection(l.id)}
              className="link-gold text-white/90 hover:text-white text-xs tracking-luxe uppercase font-light transition-colors">
              {l.label}
            </button>
          ))}
          {pages.map((l) => (
            <button key={l.path} data-testid={`nav-${l.path.replace("/", "")}`} onClick={() => goToPage(l.path)}
              className="link-gold text-white/90 hover:text-white text-xs tracking-luxe uppercase font-light transition-colors">
              {l.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-4">
          <button
            data-testid="lang-toggle"
            onClick={() => { trackEvent("language_toggle"); toggle(); }}
            className="flex items-center gap-2 text-white/90 hover:text-gold transition-colors"
            title={lang === "en" ? "Traduire en français" : "Translate to English"}
          >
            <span className="inline-flex w-7 h-5 overflow-hidden border border-white/30 rounded-sm">
              <span className="flex-1 bg-frblue" /><span className="flex-1 bg-white" /><span className="flex-1 bg-frred" />
            </span>
            <span className="text-xs tracking-widest uppercase font-medium">{lang === "en" ? "FR" : "EN"}</span>
          </button>
          <button
            data-testid="nav-inquire"
            onClick={() => goToSection("contact")}
            className="btn-lux hidden sm:inline-flex border border-gold text-gold hover:bg-gold hover:text-navy px-5 py-2 text-xs tracking-luxe uppercase"
          >
            {t.nav.contact}
          </button>
          <button data-testid="nav-mobile-toggle" onClick={() => setOpen(!open)} className="lg:hidden text-white text-2xl leading-none">
            {open ? "\u00d7" : "\u2261"}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-navy/98 border-t border-gold/20 mt-3">
          <div className="flex flex-col px-6 py-4 gap-4">
            {sections.map((l) => (
              <button key={l.id} onClick={() => goToSection(l.id)} className="text-left text-white/90 text-sm tracking-luxe uppercase">{l.label}</button>
            ))}
            {pages.map((l) => (
              <button key={l.path} onClick={() => goToPage(l.path)} className="text-left text-white/90 text-sm tracking-luxe uppercase">{l.label}</button>
            ))}
            <button onClick={() => goToSection("contact")} className="text-left text-gold text-sm tracking-luxe uppercase">{t.nav.contact}</button>
          </div>
        </div>
      )}
    </motion.nav>
  );
}
